import { useEffect, type CSSProperties, type ReactNode } from 'react'
import { getPillar } from './studioConstants.js'
import type { BannerMessage, BannerTone } from './types'

type StyleMap = Record<string, CSSProperties>
// `S` exposes a couple of style functions (e.g. truthBadge(tone)) alongside
// plain CSSProperties — keep it permissive until ui-kit migration.
type StyleSheet = StyleMap & Record<string, unknown>

export function Spinner() {
  return (
    <div
      style={{
        display: 'flex',
        gap: 5,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '38px 0',
      }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            background: '#d4582d',
            animation: `pulse 1.4s ${i * 0.2}s infinite ease-in-out both`,
          }}
        />
      ))}
    </div>
  )
}

export interface ToastProps {
  msg: string
  onDone: () => void
  S: StyleSheet
}

export function Toast({ msg, onDone, S }: ToastProps) {
  useEffect(() => {
    if (!msg) return
    const t = setTimeout(onDone, 2500)
    return () => clearTimeout(t)
  }, [msg, onDone])
  if (!msg) return null
  return <div style={S.toast as CSSProperties}>{msg}</div>
}

const BANNER_TONE_STYLES: Record<
  BannerTone,
  { background: string; borderColor: string; color: string }
> = {
  error: {
    background: 'rgba(222, 123, 114, 0.08)',
    borderColor: 'rgba(222, 123, 114, 0.22)',
    color: '#de7b72',
  },
  warning: {
    background: 'rgba(215, 162, 100, 0.08)',
    borderColor: 'rgba(215, 162, 100, 0.22)',
    color: '#d7a264',
  },
  info: {
    background: 'rgba(138, 169, 255, 0.06)',
    borderColor: 'rgba(138, 169, 255, 0.18)',
    color: '#a7b1c2',
  },
  success: {
    background: 'rgba(138, 215, 153, 0.08)',
    borderColor: 'rgba(138, 215, 153, 0.22)',
    color: '#8ad799',
  },
}

export interface PersistentBannerProps {
  messages: BannerMessage[]
  onDismiss: (id: string) => void
}

export function PersistentBanner({
  messages,
  onDismiss,
}: PersistentBannerProps) {
  if (!messages || messages.length === 0) return null

  return (
    <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
      {messages.map((msg) => {
        const tone = BANNER_TONE_STYLES[msg.tone] || BANNER_TONE_STYLES.info
        return (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: '10px 14px',
              borderRadius: 10,
              background: tone.background,
              border: `1px solid ${tone.borderColor}`,
              color: tone.color,
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
          >
            <span style={{ flex: 1 }}>{msg.text}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {msg.action ? (
                <button
                  onClick={() => msg.action!.onClick(msg.id)}
                  disabled={msg.action.disabled}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${tone.color}`,
                    color: tone.color,
                    cursor: msg.action.disabled ? 'wait' : 'pointer',
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    opacity: msg.action.disabled ? 0.6 : 1,
                  }}
                >
                  {msg.action.label}
                </button>
              ) : null}
              {msg.dismissable && (
                <button
                  onClick={() => onDismiss(msg.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: tone.color,
                    cursor: 'pointer',
                    padding: '0 4px',
                    fontSize: 14,
                    lineHeight: 1,
                    opacity: 0.7,
                  }}
                  aria-label="Dismiss"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export interface PillarDotProps {
  pillarId: string | null | undefined
  size?: number
}

export function PillarDot({ pillarId, size = 7 }: PillarDotProps) {
  const p = getPillar(pillarId)
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: p.color,
        display: 'inline-block',
        marginRight: 6,
        flexShrink: 0,
      }}
    />
  )
}

function getQaTone(
  status: string | undefined
): 'success' | 'error' | 'warning' {
  if (status === 'pass') return 'success'
  if (status === 'fail') return 'error'
  return 'warning'
}

export interface ContentQaIssue {
  id: string
  severity?: string
  message: string
}

export interface ContentQaPanelProps {
  qa: {
    status?: string
    score?: number
    issues?: ContentQaIssue[]
  } | null
  S: StyleSheet
  compact?: boolean
}

export function ContentQaPanel({
  qa,
  S,
  compact = false,
}: ContentQaPanelProps) {
  if (!qa) return null

  const issues = Array.isArray(qa.issues) ? qa.issues : []
  const visibleIssues = compact ? issues.slice(0, 3) : issues.slice(0, 5)

  // S.truthBadge is a function in the legacy style helpers; the cast
  // is a deliberate ui-kit migration tag — Wave 17 replaces this with
  // a typed Badge component.
  const truthBadge = S.truthBadge as
    | ((tone: string) => CSSProperties)
    | undefined

  return (
    <div
      style={
        {
          ...(S.infoBox as CSSProperties),
          marginTop: compact ? 10 : 12,
        } as CSSProperties
      }
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: visibleIssues.length ? 10 : 0,
        }}
      >
        <div style={S.sectionTitle as CSSProperties}>Content QA</div>
        <span style={truthBadge ? truthBadge(getQaTone(qa.status)) : undefined}>
          {qa.status} · {qa.score}/100
        </span>
      </div>
      {visibleIssues.length ? (
        <div style={{ display: 'grid', gap: 6 }}>
          {visibleIssues.map(
            (issue: ContentQaIssue): ReactNode => (
              <div key={issue.id} style={S.subText as CSSProperties}>
                {issue.severity}: {issue.message}
              </div>
            )
          )}
          {issues.length > visibleIssues.length && (
            <div style={S.subText as CSSProperties}>
              {issues.length - visibleIssues.length} more issue
              {issues.length - visibleIssues.length === 1 ? '' : 's'}
            </div>
          )}
        </div>
      ) : (
        <div style={S.subText as CSSProperties}>
          No strategy or format warnings detected.
        </div>
      )}
    </div>
  )
}
